import { message } from "antd";

import { useState, useEffect } from "react";
import PersonalInformation from "./Steps/PersonalInformation";
import VerifyBusiness from "./Steps/VerifyBusiness";
import PreviewBusiness from "./Steps/PreviewBusiness";
import { GetStartedSteps } from "./types";
import styles from "./GetStarted.module.scss";
import HeaderTitle from "../../Components/HeaderTitle";
import { postFormDataWithDotNet } from "../../../../../newApis/dotNetApiMethods";
import { CustomerOnboardingType, LegalEntity } from "../../interfaces";
import * as ajaxEndpoints from "../../../../../api/ajax-endpoints";
import { errorHandler } from "../../../../../helpers/errorHandler";

interface IGetStarted {
  onContinue: (data: any) => void;
  onBack?: () => void;
  onBackHandlerReady?: (handler: (() => void) | null) => void;
  defaultValues?: any;
}

const GetStarted: React.FC<IGetStarted> = ({
  onContinue,
  onBack,
  onBackHandlerReady,
  defaultValues = {},
}) => {
  const [currentSubStep, setCurrentSubStep] = useState<GetStartedSteps>(
    GetStartedSteps.PERSONAL_INFORMATION,
  );
  const [subStepData, setSubStepData] = useState<any>(defaultValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNextSubStep = (step: GetStartedSteps, data?: any) => {
    if (data) {
      setSubStepData((prev: any) => ({ ...prev, ...data }));
    }
    setCurrentSubStep(step);
  };

  const handleBackSubStep = () => {
    // Navigate backwards through sub-steps
    console.log("currentSubStep:", currentSubStep);
    switch (currentSubStep) {
      case GetStartedSteps.PREVIEW_BUSINESS:
        setCurrentSubStep(GetStartedSteps.VERIFY_BUSINESS);
        break;
      case GetStartedSteps.VERIFY_BUSINESS:
        setCurrentSubStep(GetStartedSteps.PERSONAL_INFORMATION);
        break;
      case GetStartedSteps.PERSONAL_INFORMATION:
        // We're at the first sub-step, go back to parent step
        if (onBack) {
          onBack();
        }
        break;
      default:
        break;
    }
  };

  // Expose the back handler to parent via callback
  useEffect(() => {
    if (onBackHandlerReady) {
      onBackHandlerReady(handleBackSubStep);
    }
    // Cleanup: unregister handler when component unmounts
    return () => {
      if (onBackHandlerReady) {
        onBackHandlerReady(null);
      }
    };
  }, [currentSubStep]);

  const handleComplete = async (data?: any) => {
    const finalData = data ? { ...subStepData, ...data } : subStepData;

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      formData.append("EffectiveDate", finalData?.effectiveDate);
      formData.append("Firstname", finalData?.firstname);
      formData.append("Lastname", finalData?.lastname);
      formData.append(
        "PhoneNumber",
        finalData?.phoneNumber?.startsWith("0")
          ? finalData?.phoneNumber
          : `0${finalData?.phoneNumber}`,
      );
      formData.append("CountryCode", finalData?.countryCode);
      formData.append("Email", finalData?.email);
      formData.append("CustomerSubType", String(finalData?.customerSubType));
      formData.append("UserPosition", String(finalData?.userPosition));
      formData.append("BusinessName", finalData?.businessName);
      formData.append(
        "BusinessRegistrationNumber",
        finalData?.businessRegistrationNumber,
      );
      formData.append("Bvn", finalData?.bvn);
      formData.append(
        "OnboardingType",
        String(CustomerOnboardingType.DigitalBank),
      );
      formData.append("LegalEntityId", String(LegalEntity.MfbAndTech));

      const response = await postFormDataWithDotNet(
        ajaxEndpoints.CREATE_BUSINESS,
        formData,
      );

      message.success(
        response?.data?.message || "Business account created successfully!",
      );
      onContinue({
        funder_id: response?.funder_id,
        ...finalData,
      });
    } catch (error) {
      const errorMessage = errorHandler(error);
      message.error(errorMessage || "Failed to create business account");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (currentSubStep) {
      case GetStartedSteps.PREVIEW_BUSINESS:
        return "Review your information";
      default:
        return "Let's get started";
    }
  };

  const renderSubStep = () => {
    switch (currentSubStep) {
      case GetStartedSteps.PERSONAL_INFORMATION:
        return (
          <PersonalInformation
            handleNextStep={handleNextSubStep}
            defaultValues={subStepData}
          />
        );

      case GetStartedSteps.VERIFY_BUSINESS:
        return (
          <VerifyBusiness
            handleNextStep={handleNextSubStep}
            defaultValues={subStepData}
          />
        );

      case GetStartedSteps.PREVIEW_BUSINESS:
        return (
          <PreviewBusiness
            formData={subStepData}
            onComplete={handleComplete}
            isSubmitting={isSubmitting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.getStarted}>
      <HeaderTitle>{getTitle()}</HeaderTitle>
      {renderSubStep()}
    </div>
  );
};

export default GetStarted;
